import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HiOutlineDocumentText, HiOutlineArrowLeft, HiOutlineCheckCircle, HiOutlineGift } from 'react-icons/hi';

export default function TeklifDegerlendirPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [showPdf, setShowPdf] = useState(false);
  const [promotions, setPromotions] = useState({});
  const [showProformaModal, setShowProformaModal] = useState(false);
  const [tempWarnings, setTempWarnings] = useState({});
  const [proformaData, setProformaData] = useState({
    partner_id: '',
    request_date: new Date().toISOString().split('T')[0],
    delivery_date: '',
    notes: ''
  });
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    setLoading(true);
    // Ürün listesini yükle
    fetch('/api/products')
      .then(res => res.json())
      .then(data => setProducts(data))
      .catch(() => setProducts([]));

    // Partner listesini yükle
    fetch('/api/partners')
      .then(res => res.json())
      .then(data => setPartners(data))
      .catch(() => setPartners([]));

    // Teklif detaylarını yükle
    fetch(`/api/guest-offers/${id}`)
      .then(res => res.json())
      .then(data => {
        setOffer(data);
      })
      .finally(() => setLoading(false));
  }, [id]);

  // Ürün fiyatını bul
  const getProductPrice = (productName) => {
    const product = products.find(p => p.name === productName);
    return product ? product.price : 0;
  };

  // Promosyon miktarını güncelle
  const handlePromotionChange = (itemKey, value) => {
    const numValue = parseInt(value) || 0;
    const item = offer.items.find(item => `${item.name}-${item.measure}-${item.qty}` === itemKey);
    const costLimit = calculateCostLimit(item);
    
    if (numValue > costLimit) {
      alert(`⚠️ Uyarı: Promosyon miktarı ${costLimit} adeti geçemez!\n\nPromosyon sınırı: %70 (${costLimit} adet)\nGirdiğiniz: ${numValue} adet\n\nLütfen daha düşük bir değer girin.`);
      return;
    }
    
    if (numValue === costLimit && costLimit > 0) {
      // Başa baş maliyet uyarısını 3 saniye göster
      setTempWarnings(prev => ({ ...prev, [itemKey]: true }));
      setTimeout(() => {
        setTempWarnings(prev => ({ ...prev, [itemKey]: false }));
      }, 3000);
    } else {
      // Uyarıyı hemen kaldır
      setTempWarnings(prev => ({ ...prev, [itemKey]: false }));
    }
    
    setPromotions(prev => ({ ...prev, [itemKey]: numValue }));
  };

  // Maliyet hesaplama fonksiyonu (%70'e kadar promosyon)
  const calculateCostLimit = (item) => {
    const product = products.find(p => p.name === item.name);
    if (!product) return 0;
    
    // Ürün adedinin %70'ine kadar promosyon ürün tanımlanabilir
    const promotionLimit = Math.floor(item.qty * 0.7);
    return promotionLimit;
  };

  // Promosyon maliyet kontrolü
  const checkPromotionCost = (item) => {
    const costLimit = calculateCostLimit(item);
    const itemKey = `${item.name}-${item.measure}-${item.qty}`;
    const currentPromotion = promotions[itemKey] || 0;
    
    if (currentPromotion > costLimit) {
      return {
        isOverLimit: true,
        limit: costLimit,
        current: currentPromotion,
        warning: `Bu ürün için maksimum ${costLimit} adet promosyon ürün verilebilir (ürün adedinin %70'i)`
      };
    }
    
    return {
      isOverLimit: false,
      limit: costLimit,
      current: currentPromotion
    };
  };

  // Promosyon sonrası birim fiyatını hesapla
  const calculatePromotionCost = (item) => {
    const product = products.find(p => p.name === item.name);
    if (!product) return 0;
    
    const originalQuantity = item.qty || 0;
    const itemKey = `${item.name}-${item.measure}-${item.qty}`;
    const promotionQuantity = promotions[itemKey] || 0;
    const totalQuantity = originalQuantity + promotionQuantity;
    
    if (totalQuantity === 0) return 0;
    
    // Toplam maliyet: (orijinal adet × birim fiyat) - (promosyon adet × birim fiyat)
    // Çünkü promosyon ürünler bedava veriliyor
    const originalCost = originalQuantity * product.price;
    const promotionCost = promotionQuantity * product.price;
    const totalCost = originalCost - promotionCost; // Promosyon maliyeti azaltır
    
    // Promosyon sonrası birim fiyatı = Toplam maliyet / Toplam adet
    const newUnitPrice = totalCost / totalQuantity;
    
    return newUnitPrice;
  };

  // Toplam ürün adedini hesapla (orijinal + promosyon)
  const calculateTotalQuantity = (item) => {
    const originalQuantity = item.qty || 0;
    const itemKey = `${item.name}-${item.measure}-${item.qty}`;
    const promotionQuantity = promotions[itemKey] || 0;
    return originalQuantity + promotionQuantity;
  };

  // Proforma oluştur
  const handleCreateProforma = async () => {
    // Eksik bilgileri kontrol et
    if (!proformaData.partner_id) {
      alert('Lütfen partner seçiniz.');
      return;
    }
    if (!proformaData.delivery_date) {
      alert('Lütfen teslimat tarihi belirtiniz.');
      return;
    }

    // Promosyon maliyet kontrolü
    const overLimitItems = [];
    offer.items.forEach(item => {
      const costCheck = checkPromotionCost(item);
      if (costCheck.isOverLimit) {
        overLimitItems.push({
          name: item.name,
          limit: costCheck.limit,
          current: costCheck.current
        });
      }
    });

    if (overLimitItems.length > 0) {
      const warningMessage = overLimitItems.map(item => 
        `${item.name}: ${item.current} adet (limit: ${item.limit} adet)`
      ).join('\n');
      
      const confirmMessage = `Aşağıdaki ürünler için promosyon miktarı maliyet limitini aşıyor:\n\n${warningMessage}\n\nDevam etmek istiyor musunuz?`;
      
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    try {
      // Proforma oluştur
      const proformaResponse = await fetch('/api/proformas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          proforma_no: `PF-${Date.now()}`,
          partner_id: proformaData.partner_id,
          request_date: proformaData.request_date,
          delivery_date: proformaData.delivery_date,
          total_amount: calculateTotalAmount(),
          currency: 'TRY',
          status: 'pending',
          notes: proformaData.notes
        })
      });

      if (!proformaResponse.ok) {
        throw new Error('Proforma oluşturulamadı');
      }

      const proforma = await proformaResponse.json();

      // Proforma items oluştur
      let createdItems = 0;
      let failedItems = 0;
      
      for (const item of offer.items) {
        const product = products.find(p => p.name === item.name);
        if (product) {
          try {
            const response = await fetch('/api/proforma-items', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                proforma_id: proforma.id,
                product_id: product.id,
                quantity: item.qty,
                unit_price: product.price,
                total_price: product.price * item.qty,
                notes: item.aciklama || '',
                promotion_quantity: promotions[`${item.name}-${item.measure}-${item.qty}`] || 0
              })
            });
            
            if (response.ok) {
              createdItems++;
            } else {
              failedItems++;
              console.error(`Proforma item oluşturulamadı: ${item.name}`);
            }
          } catch (error) {
            failedItems++;
            console.error(`Proforma item hatası: ${item.name} - ${error.message}`);
          }
        } else {
          failedItems++;
          console.error(`Ürün bulunamadı: ${item.name}`);
        }
      }
      
      console.log(`Proforma items: ${createdItems} başarılı, ${failedItems} başarısız`);

      if (failedItems === 0) {
        alert('Proforma başarıyla oluşturuldu!');
        setShowProformaModal(false);
        navigate('/admin/proforma');
      } else {
        alert(`Proforma oluşturuldu ancak ${failedItems} ürün eklenemedi. Detaylar için konsolu kontrol edin.`);
        setShowProformaModal(false);
        navigate('/admin/proforma');
      }
    } catch (error) {
      alert('Proforma oluşturulamadı: ' + error.message);
    }
  };

  // Toplam tutarı hesapla
  const calculateTotalAmount = () => {
    let total = 0;
    offer?.items?.forEach(item => {
      const product = products.find(p => p.name === item.name);
      if (product) {
        total += product.price * item.qty;
      }
    });
    return total;
  };

  if (loading) return <div className="flex justify-center items-center h-40 text-blue-700 text-lg font-semibold animate-pulse">Yükleniyor...</div>;
  if (!offer) return <div className="text-red-600 font-bold">Sipariş bulunamadı.</div>;

  return (
    <div className="w-full p-6">
      <div className="flex items-center gap-3 mb-8">
        <button className="p-2 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200" onClick={() => navigate(-1)} title="Geri Dön"><HiOutlineArrowLeft className="w-6 h-6" /></button>
        <h1 className="text-3xl font-bold text-blue-900 flex items-center gap-3 px-6 pt-6">
          <HiOutlineDocumentText className="w-8 h-8 text-blue-700" /> Siparişi Değerlendir
        </h1>
      </div>
      <div className="bg-white rounded-2xl shadow-xl p-8 border border-blue-100">
        <div className="mb-6">
          <div className="text-blue-900 font-semibold mb-1">Firma Bilgileri</div>
          <div className="flex flex-wrap gap-4 text-sm mb-2">
            <span><b>Firma:</b> {offer.firma?.firma || offer.firma}</span>
            <span><b>Yetkili:</b> {offer.firma?.yetkili || offer.yetkili}</span>
            <span><b>Telefon:</b> {offer.firma?.telefon || offer.telefon}</span>
            <span><b>Email:</b> {offer.firma?.email || offer.email}</span>
            <span><b>Şehir:</b> {offer.firma?.sehir || offer.sehir}</span>
          </div>
        </div>
        <div>
          <div className="text-blue-900 font-semibold mb-2">Ürünler ve Fiyatlandırma</div>
          <table className="min-w-full border text-sm mb-4">
            <thead>
              <tr>
                <th className="border px-2 py-1">Ürün Adı</th>
                <th className="border px-2 py-1">Ölçü</th>
                <th className="border px-2 py-1">Adet</th>
                <th className="border px-2 py-1">Açıklama</th>
                <th className="border px-2 py-1">Ürün Fiyatı (₺)</th>
                <th className="border px-2 py-1">Promosyon Adet</th>
                <th className="border px-2 py-1">Toplam Adet</th>
                <th className="border px-2 py-1">Birim Fiyat (₺)</th>
              </tr>
            </thead>
            <tbody>
              {offer.items.map(item => {
                const costLimit = calculateCostLimit(item);
                const costCheck = checkPromotionCost(item);
                const itemKey = `${item.name}-${item.measure}-${item.qty}`;
                
                return (
                  <tr key={itemKey} className={costCheck.isOverLimit ? 'bg-red-50' : ''}>
                    <td className="border px-2 py-1 font-semibold text-blue-900">{item.name}</td>
                    <td className="border px-2 py-1">{item.measure}</td>
                    <td className="border px-2 py-1">{item.qty}</td>
                    <td className="border px-2 py-1">{item.aciklama}</td>
                    <td className="border px-2 py-1">
                      <div className="w-24 px-2 py-1 text-sm font-medium text-gray-900">
                        {getProductPrice(item.name).toLocaleString('tr-TR')} ₺
                      </div>
                    </td>
                    <td className="border px-2 py-1">
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max={costLimit}
                          className={`w-20 px-2 py-1 border rounded focus:ring-2 focus:ring-blue-200 text-center ${
                            costCheck.isOverLimit ? 'border-red-500 bg-red-50' : 
                            tempWarnings[itemKey] ? 'border-orange-500 bg-orange-50' : ''
                          }`}
                          placeholder="0"
                          value={promotions[itemKey] || ''}
                          onChange={e => handlePromotionChange(itemKey, e.target.value)}
                        />
                        {costCheck.isOverLimit && (
                          <div className="absolute top-full left-0 right-0 mt-1 p-2 bg-red-100 border border-red-300 rounded text-xs text-red-700 z-10">
                            ⚠️ {costCheck.warning}
                          </div>
                        )}
                        {tempWarnings[itemKey] && (
                          <div className="absolute top-full left-0 right-0 mt-1 p-2 bg-orange-100 border border-orange-300 rounded text-xs text-orange-700 z-10">
                            ⚠️ BAŞA BAŞ MALİYET UYARISI! %70 promosyon sınırına ulaştınız!
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="border px-2 py-1">
                      <div className="text-center">
                        <div className="text-sm font-medium text-blue-900">
                          {calculateTotalQuantity(item)} adet
                        </div>
                        {(promotions[itemKey] || 0) > 0 && (
                          <div className="text-xs text-gray-500">
                            ({item.qty} + {promotions[itemKey] || 0})
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="border px-2 py-1">
                      <div className="text-center">
                        <div className="text-sm font-medium text-gray-900">
                          {calculatePromotionCost(item).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                        </div>
                        {(promotions[itemKey] || 0) > 0 && (
                          <div className="text-xs text-gray-500">
                            ({(item.qty * getProductPrice(item.name)).toLocaleString('tr-TR')} ₺ ÷ {calculateTotalQuantity(item)} adet)
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700 mb-2">
              <strong>Not:</strong> Fiyatlar ürün listesindeki güncel fiyatlardan otomatik olarak alınmaktadır.
            </p>
            <p className="text-sm text-blue-700">
              <strong>Promosyon Ürün Hesaplama:</strong> Her ürün için maksimum promosyon miktarı, ürün adedinin %70'i olarak belirlenir. Bu limiti aşan promosyon miktarları için uyarı gösterilir. %70 sınıra ulaştığında başa baş maliyet uyarısı gösterilir.
            </p>
          </div>
          
          {/* Proformaya Dönüştür Butonu */}
          <div className="mt-6 flex justify-center">
            <button
              className="px-8 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition flex items-center gap-2"
              onClick={() => setShowProformaModal(true)}
            >
              <HiOutlineCheckCircle className="w-5 h-5" />
              Proformaya Dönüştür
            </button>
          </div>
          
          {/* Maliyet Uyarısı */}
          {(() => {
            const overLimitItems = offer.items.filter(item => checkPromotionCost(item).isOverLimit);
            if (overLimitItems.length > 0) {
              return (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700 mb-2">
                    <HiOutlineGift className="w-5 h-5" />
                    <span className="font-semibold">Maliyet Uyarısı</span>
                  </div>
                  <p className="text-sm text-red-600">
                    {overLimitItems.length} ürün için promosyon miktarı %70 sınırını aşıyor. 
                    Bu durumda zarar riski bulunmaktadır. Lütfen promosyon miktarlarını kontrol ediniz.
                  </p>
                </div>
              );
            }
            return null;
          })()}
        </div>
        {/* PDF Önizleme */}
        {showPdf && (
          <div className="mt-10">
            <div className="text-blue-900 font-semibold mb-2">PDF Önizlemesi</div>
            <iframe
              src={`/api/guest-offers/${id}/pdf`}
              title="Sipariş PDF Önizleme"
              className="w-full min-h-[600px] border rounded-xl shadow"
            />
          </div>
        )}
      </div>

      {/* Proforma Oluşturma Modalı */}
      {showProformaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl mx-4">
            <div className="text-2xl font-bold text-blue-900 text-center mb-6">
              Proforma Oluştur
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Partner Seçin *</label>
                <select
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={proformaData.partner_id}
                  onChange={(e) => setProformaData(prev => ({ ...prev, partner_id: e.target.value }))}
                >
                  <option value="">Partner seçiniz</option>
                  {partners.map(partner => (
                    <option key={partner.id} value={partner.id}>
                      {partner.name} ({partner.code})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Talep Tarihi</label>
                  <input
                    type="date"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={proformaData.request_date}
                    onChange={(e) => setProformaData(prev => ({ ...prev, request_date: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Teslimat Tarihi *</label>
                  <input
                    type="date"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={proformaData.delivery_date}
                    onChange={(e) => setProformaData(prev => ({ ...prev, delivery_date: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Notlar</label>
                <textarea
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                  placeholder="Proforma notları..."
                  value={proformaData.notes}
                  onChange={(e) => setProformaData(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-2">Özet Bilgiler</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <div>Toplam Ürün: {offer.items.length} adet</div>
                  <div>Toplam Tutar: {calculateTotalAmount().toLocaleString('tr-TR')} ₺</div>
                  <div>Promosyon Ürünler: {Object.values(promotions).filter(v => v > 0).length} adet</div>
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <div className="font-medium text-gray-700 mb-1">Promosyon Maliyet Kontrolü:</div>
                    {(() => {
                      const overLimitItems = offer.items.filter(item => checkPromotionCost(item).isOverLimit);
                      if (overLimitItems.length > 0) {
                        return (
                          <div className="text-red-600">
                            ⚠️ {overLimitItems.length} ürün için %70 promosyon sınırı aşıldı
                          </div>
                        );
                      } else {
                        return (
                          <div className="text-green-600">
                            ✅ Tüm promosyon miktarları %70 sınırı içinde
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                className="flex-1 px-4 py-3 bg-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-400 transition"
                onClick={() => setShowProformaModal(false)}
              >
                İptal
              </button>
              <button
                className="flex-1 px-4 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition"
                onClick={handleCreateProforma}
              >
                Proforma Oluştur
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 